import type { LucideIcon } from "lucide-react";
import {
  LayoutGrid, PlayCircle, Cloud, KeyRound, Boxes, Sparkles, Database,
  Activity, Wallet, Settings, BookOpen, LifeBuoy, ShieldCheck, Bell,
} from "lucide-react";

// Single source of truth for app navigation (sidebar, mobile sheet, quick search).

export interface NavLeaf {
  name: string;
  href: string;
  keywords?: string;
}

export interface NavItem {
  name: string;
  icon: LucideIcon;
  href?: string;
  children?: NavLeaf[];
  adminOnly?: boolean;
}

export const primaryNav: NavItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutGrid },
  { name: "Playground", href: "/playground", icon: PlayCircle },
  {
    name: "Providers",
    icon: Cloud,
    children: [
      { name: "All providers", href: "/providers" },
      { name: "Model subscriptions", href: "/model-subscriptions" },
      { name: "Provider API keys", href: "/provider-api-keys" },
      { name: "Fallback chains", href: "/fallback-chains", keywords: "failover routing backup" },
    ],
  },
  {
    name: "Developers",
    icon: KeyRound,
    children: [
      { name: "API keys", href: "/api-keys" },
      { name: "API integration", href: "/api-integration" },
      { name: "Integrations", href: "/integrations" },
    ],
  },
  {
    name: "AI Studio",
    icon: Sparkles,
    children: [
      { name: "LangChain", href: "/langchain" },
      { name: "LangGraph", href: "/langgraph" },
      { name: "Agents", href: "/agents" },
      { name: "RAG", href: "/rag", keywords: "knowledge base retrieval" },
    ],
  },
  {
    name: "Data",
    icon: Database,
    children: [
      { name: "Datasets", href: "/datasets" },
      { name: "Fine-tuning", href: "/fine-tuning" },
    ],
  },
  {
    name: "Observability",
    icon: Activity,
    children: [
      { name: "Analytics", href: "/analytics" },
      { name: "Usage", href: "/usage" },
      { name: "Tracing", href: "/tracing" },
      { name: "Guardrails", href: "/guardrails" },
      { name: "A/B testing", href: "/ab-testing" },
    ],
  },
  {
    name: "Wallet & usage",
    icon: Wallet,
    children: [
      { name: "Wallet", href: "/billing", keywords: "billing credit balance top up redeem coupon" },
      { name: "Consumption", href: "/consumption" },
      { name: "Cost optimization", href: "/cost-optimization" },
    ],
  },
  {
    name: "Admin",
    icon: ShieldCheck,
    adminOnly: true,
    children: [
      { name: "Team", href: "/team" },
      { name: "Invitations", href: "/invitations" },
      { name: "Audit logs", href: "/admin/audit-logs" },
      { name: "SLA dashboard", href: "/admin/sla" },
      { name: "Provider configs", href: "/admin/provider-configs" },
      { name: "Coupons", href: "/admin/coupons", keywords: "credit codes grant wallet" },
      { name: "System settings", href: "/admin/settings" },
    ],
  },
];

export const secondaryNav: NavItem[] = [
  { name: "Notifications", href: "/notifications", icon: Bell },
  { name: "Organizations", href: "/organizations", icon: Boxes },
  { name: "Settings", href: "/settings", icon: Settings },
  { name: "Documentation", href: "/docs", icon: BookOpen },
  { name: "Support", href: "/docs/get-started/faqs", icon: LifeBuoy },
];

export const visibleNav = (items: NavItem[], isAdmin: boolean) => items.filter((i) => !i.adminOnly || isAdmin);

/** Flattened leaf list for quick search. */
export const searchableNav = (isAdmin: boolean): NavLeaf[] =>
  [...visibleNav(primaryNav, isAdmin), ...secondaryNav].flatMap((i) =>
    i.children ? i.children.map((c) => ({ ...c, keywords: `${i.name} ${c.keywords ?? ""}` })) : [{ name: i.name, href: i.href! }]
  );

export const isPathActive = (pathname: string, href: string) =>
  pathname === href || (href !== "/docs" && pathname.startsWith(`${href}/`));

// On touch screens (coarse pointer) give every control in the shell and pages
// a >= 40px hit area. Mouse layouts keep the compact desktop sizing.
export const TOUCH_TARGETS = [
  "pointer-coarse:[&_button:not([role=switch]):not([role=checkbox])]:min-h-10",
  "pointer-coarse:[&_button:not([role=switch]):not([role=checkbox])]:min-w-10",
  "pointer-coarse:**:[[role=tab]]:min-h-10",
  "pointer-coarse:[&_a.flex]:min-h-10",
  "pointer-coarse:[&_a.inline-flex]:min-h-10",
  "pointer-coarse:[&_input:not([type=checkbox]):not([type=radio])]:min-h-10",
].join(" ");
